import { GridState } from "../types";
import {
  getIEEE14SystemBrowser,
  getIEEE9SystemBrowser,
} from "./networks.browser";

export type ScenarioType = "ieee14" | "ieee9" | "simple" | "congestion";

export interface Scenario {
  id: ScenarioType;
  name: string;
  description: string;
  gridState: GridState;
}

/**
 * Create a simple scenario with one generator and one load
 */
function createSimpleScenario(): GridState {
  return {
    nodes: [
      {
        id: "gen1",
        type: "generator",
        x: 200,
        y: 150,
        minPowerMW: 10,
        maxPowerMW: 150,
        costPerMWh: 60,
        fuelType: "fossil",
        value: 66.6667,
        voltageAngle: 0,
        voltage: 1,
      },
      {
        id: "load1",
        type: "load",
        x: 400,
        y: 150,
        value: -60,
        voltageAngle: 6.66667,
        voltage: 1,
      },
      {
        id: "generator2",
        type: "generator",
        x: 203.14532470703125,
        y: 46.85466003417969,
        minPowerMW: 0,
        maxPowerMW: 100,
        costPerMWh: 15,
        fuelType: "wind",
        value: 0,
        voltageAngle: 6.66667,
        voltage: 1,
      },
      {
        id: "generator3",
        type: "generator",
        x: 205.74835205078125,
        y: 281.12799072265625,
        minPowerMW: 0,
        maxPowerMW: 100,
        costPerMWh: 20,
        fuelType: "solar",
        value: 0,
        voltageAngle: 6.66667,
        voltage: 1,
      },
      {
        id: "battery4",
        type: "battery",
        x: 210.95443725585938,
        y: 398.2646484375,
        maxChargeMW: 205,
        maxDischargeMW: 95,
        maxEnergyMWh: 750,
        minEnergyMWh: 0,
        efficiency: 0.9,
        initialEnergyMWh: 410,
        value: -6.66667,
        voltageAngle: 7.33333,
        voltage: 1,
      },
      {
        id: "bus5",
        type: "bus",
        x: 320,
        y: 150,
        value: 0,
        voltageAngle: 3.33333,
        voltage: 1,
      },
    ],
    edges: [
      {
        id: "line1",
        from: "gen1",
        to: "bus5",
        resistance: 0.05,
        rating: 200,
        current: 0.666667,
      },
      {
        id: "edge2",
        from: "generator3",
        to: "bus5",
        resistance: 0.1,
        rating: 100,
        current: 0,
      },
      {
        id: "edge3",
        from: "generator2",
        to: "bus5",
        resistance: 0.1,
        rating: 100,
        current: 0,
      },
      {
        id: "edge4",
        from: "battery4",
        to: "bus5",
        resistance: 0.1,
        rating: 100,
        current: 0.0666667,
      },
      {
        id: "edge5",
        from: "bus5",
        to: "load1",
        resistance: 0.05,
        rating: 200,
        current: 0.666667,
      },
    ],
  };
}

/**
 * Create a scenario that demonstrates transmission congestion.
 *
 * Topology: two generators each connected to a single load by their own line.
 *
 *   gen1 (cheap) --line1 (rating 100 MW)--> load1
 *   gen2 (pricey) --line2 (rating 200 MW)--> load1
 *
 * gen1 is cheap and has plenty of capacity, so absent any network limits it
 * would serve the entire load on its own. But line1 (gen1 -> load1) is rated
 * at only 100 MW, while the default load curve peaks at 150 MW. Once demand
 * climbs above 100 MW the line into load1 saturates, and the extra demand can
 * only be met by dispatching the expensive gen2 over the un-congested line2.
 * That out-of-merit dispatch of gen2 is the signature of congestion.
 */
function createCongestionScenario(): GridState {
  return {
    nodes: [
      {
        id: "gen1",
        type: "generator",
        x: 150,
        y: 150,
        minPowerMW: 0,
        maxPowerMW: 200,
        costPerMWh: 20, // cheap
        fuelType: "fossil",
        value: 0,
        voltageAngle: 0,
        voltage: 1,
      },
      {
        id: "gen2",
        type: "generator",
        x: 150,
        y: 350,
        minPowerMW: 0,
        maxPowerMW: 200,
        costPerMWh: 80, // expensive
        fuelType: "fossil",
        value: 0,
        voltageAngle: 0,
        voltage: 1,
      },
      {
        id: "load1",
        type: "load",
        x: 500,
        y: 250,
        value: -150,
        voltageAngle: 0,
        voltage: 1,
      },
    ],
    edges: [
      {
        id: "line1",
        from: "gen1",
        to: "load1",
        resistance: 0.05,
        reactance: 0.1,
        rating: 100, // congested at peak load (curve peaks at 150 MW)
        current: 0,
      },
      {
        id: "line2",
        from: "gen2",
        to: "load1",
        resistance: 0.05,
        reactance: 0.1,
        rating: 200, // un-congested backup path
        current: 0,
      },
    ],
  };
}

/**
 * Get all available scenarios for browser environment
 */
export function getAvailableScenarios(): Scenario[] {
  const scenarios: Scenario[] = [
    {
      id: "simple",
      name: "Simple System",
      description:
        "Basic system with one generator and one load connected by a transmission line",
      gridState: createSimpleScenario(),
    },
    {
      id: "congestion",
      name: "Transmission Congestion",
      description:
        "A cheap generator whose line to the load saturates at peak demand, forcing an expensive generator to be dispatched over an un-congested path",
      gridState: createCongestionScenario(),
    },
    {
      id: "ieee14",
      name: "IEEE 14-Bus System",
      description:
        "Standard IEEE 14-bus test system with 5 generators and 11 loads",
      get gridState() {
        return getIEEE14SystemBrowser();
      },
    },
    {
      id: "ieee9",
      name: "IEEE 9-Bus System",
      description: "",
      get gridState() {
        return getIEEE9SystemBrowser();
      },
    },
  ];

  return scenarios;
}

/**
 * Get a specific scenario by ID for browser environment
 */
export function getScenario(id: ScenarioType): Scenario | null {
  const scenarios = getAvailableScenarios();
  return scenarios.find((scenario) => scenario.id === id) || null;
}

/**
 * Narrow an arbitrary string (e.g. a URL parameter) to a known ScenarioType,
 * validating against the registered scenarios so new scenarios are accepted
 * automatically. Returns null if the value isn't a known scenario.
 */
export function parseScenarioType(value: string | null): ScenarioType | null {
  if (!value) return null;
  return getAvailableScenarios().some((scenario) => scenario.id === value)
    ? (value as ScenarioType)
    : null;
}
